import * as React from "react";
import { AnalysisSet, FvcAnalysisSet } from "webportalclient";
import { AcceptabilityActionTypes, AcceptabilityActions } from "./UseAcceptability";

export interface AcceptabilityNextGenState {
    analysisSet: FvcAnalysisSet;
    loadingBestValues: boolean;
    changedAcceptability: boolean;
}

const initialState: AcceptabilityNextGenState = {
    analysisSet: undefined,
    loadingBestValues: false,
    changedAcceptability: true,
}

export enum AcceptabilityActionTypesNextGen {
    LoadInitialAcceptabilityNextGen = "LoadInitialAcceptabilityNextGen",
    UpdateAnalysisSet = "UpdateAnalysisSet",
    UpdateBestTestNextGen = "UpdateBestTestNextGen",
    UpdateGrading = "UpdateGrading"
}

type AcceptabilityActionTypesBoth = AcceptabilityActionTypes | AcceptabilityActionTypesNextGen;

type AcceptabilityNextGenActions = LoadInitialAcceptabilityNextGenAction | UpdateAnalysisSetAction | UpdateBestTestNextGenAction | UpdateGradingAction | AcceptabilityActions;

export class LoadInitialAcceptabilityNextGenAction {
    type: AcceptabilityActionTypesNextGen.LoadInitialAcceptabilityNextGen;
    analysisSet: FvcAnalysisSet;
    comparisonAnalysisSet: FvcAnalysisSet;

    static create(analysisSet: AnalysisSet, comparisonAnalysisSet: AnalysisSet): LoadInitialAcceptabilityNextGenAction {
        return {
            type: AcceptabilityActionTypesNextGen.LoadInitialAcceptabilityNextGen,
            analysisSet: analysisSet,
            comparisonAnalysisSet: comparisonAnalysisSet,
        };
    }
}

export class UpdateAnalysisSetAction {
    type: AcceptabilityActionTypesNextGen.UpdateAnalysisSet;
    updatedAnalysisSet: FvcAnalysisSet;

    static create(updatedAnalysisSet: AnalysisSet): UpdateAnalysisSetAction {
        return {
            type: AcceptabilityActionTypesNextGen.UpdateAnalysisSet,
            updatedAnalysisSet: updatedAnalysisSet,
        };
    }
}

export class UpdateBestTestNextGenAction {
    type: AcceptabilityActionTypesNextGen.UpdateBestTestNextGen;
    bestFev1: number;
    bestFvc: number;
    bestPefMin: number;
    bestFef2575: number;
    bestFev1Fvc: number;

    static create(bestFev1: number, bestFvc: number, bestPefMin: number, bestFef2575: number, bestFev1Fvc: number): UpdateBestTestNextGenAction {
        return {
            type: AcceptabilityActionTypesNextGen.UpdateBestTestNextGen,
            bestFev1: bestFev1,
            bestFvc: bestFvc,
            bestPefMin: bestPefMin,
            bestFef2575: bestFef2575,
            bestFev1Fvc: bestFev1Fvc
        };
    }
}

export class UpdateGradingAction {
    type: AcceptabilityActionTypesNextGen.UpdateGrading;
    fev1Grade: string;
    fvcGrade: string;

    static create(fev1Grade: string, fvcGrade: string): UpdateGradingAction {
        return {
            type: AcceptabilityActionTypesNextGen.UpdateGrading,
            fev1Grade: fev1Grade,
            fvcGrade: fvcGrade
        };
    }
}

export const useAcceptabilityNextGen = (analysisSet: FvcAnalysisSet, comparisonAnalysisSet?: FvcAnalysisSet): [AcceptabilityNextGenState, React.Dispatch<any>] => {
    const reducer = (state: AcceptabilityNextGenState, action: AcceptabilityNextGenActions): AcceptabilityNextGenState => {
        switch (action.type) {
            case AcceptabilityActionTypesNextGen.LoadInitialAcceptabilityNextGen:
                const mappedState = matchOverreaderAcceptabilityToSite(state, action, comparisonAnalysisSet);

                if (mappedState)
                    return mappedState;

                return {
                    ...state,
                    analysisSet: action.analysisSet,
                    changedAcceptability: !state.changedAcceptability,
                };
            case AcceptabilityActionTypesNextGen.UpdateAnalysisSet:
                const newTests = action.updatedAnalysisSet.tests.filter(u => !state.analysisSet.tests.map(t => t.id).includes(u.id));
                return {
                    ...state,
                    analysisSet: {
                        ...state.analysisSet,
                        tests: [...state.analysisSet.tests, ...newTests]
                    }
                }
            case AcceptabilityActionTypes.ToggleTestAcceptability:
                return {
                    ...state,
                    analysisSet: {
                        ...state.analysisSet,
                        tests: state.analysisSet.tests.map(t => {
                            if (t.testId === action.testId) {
                                return {
                                    ...t,
                                    accepted: !t.accepted,
                                }
                            } else {
                                return t;
                            }
                        }),
                    },
                    changedAcceptability: !state.changedAcceptability
                };
            case AcceptabilityActionTypes.LoadingBestValues:
                return {
                    ...state,
                    loadingBestValues: true
                };
            case AcceptabilityActionTypes.DoneLoadingBestValues:
                return {
                    ...state,
                    loadingBestValues: false
                };
            case AcceptabilityActionTypesNextGen.UpdateBestTestNextGen:
                return {
                    ...state,
                    analysisSet: {
                        ...state.analysisSet,
                        bestFev1: action.bestFev1,
                        bestFvc: action.bestFvc,
                        bestPefMin: action.bestPefMin,
                        bestFef2575: action.bestFef2575,
                        bestFev1Fvc: action.bestFev1Fvc,
                    }
                };
            case AcceptabilityActionTypesNextGen.UpdateGrading:
                return {
                    ...state,
                    analysisSet: {
                        ...state.analysisSet,
                        fev1Grade: action.fev1Grade,
                        fvcGrade: action.fvcGrade
                    }
                }
            default:
                return state;
        }
    }

    const [state, dispatch] = React.useReducer(reducer, initialState);

    React.useEffect(() => {
        const hydrateAcceptability = () => {
            dispatch(LoadInitialAcceptabilityNextGenAction.create(analysisSet, comparisonAnalysisSet));
        };

        hydrateAcceptability();
    }, [analysisSet, comparisonAnalysisSet])

    return [state, dispatch];
}

export const matchOverreaderAcceptabilityToSite = (state: AcceptabilityNextGenState, action: LoadInitialAcceptabilityNextGenAction, comparisonAnalysisSet?: FvcAnalysisSet): AcceptabilityNextGenState => {
    if (comparisonAnalysisSet) {
        const notYetCompleted = action.analysisSet.firstCompleted === null;

        if (notYetCompleted) {
            return {
                ...state,
                analysisSet: {
                    ...action.analysisSet,
                    tests: state.analysisSet.tests.map(t => {
                        if (action.comparisonAnalysisSet.tests.map(cast => cast.testId).includes(t.testId)) {
                            return {
                                ...t,
                                accepted: action.comparisonAnalysisSet.tests.find(cast => cast.testId === t.testId).accepted,
                            }
                        } else {
                            return t;
                        }
                    }),
                },
                changedAcceptability: !state.changedAcceptability,
            };
        }
    } else return null;
}
